import React, { useState, useEffect } from 'react';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import axios from 'axios';
import { useAuth } from '../components/Auth/AuthContext';
import CardBalance from '../components/MyCard/CardBalance';
import { CreditCard, RefreshCw, CheckCircle, Clock, Sparkles } from 'lucide-react';

const GlobalStyle = createGlobalStyle`
  @font-face {
    font-family: 'Very Sans Light';
    src: url('/fonts/VerySans-LightText.ttf') format('truetype');
    font-weight: 300;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Very Sans Medium';
    src: url('/fonts/VerySans-MediumText.ttf') format('truetype');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Very Sans Bold';
    src: url('/fonts/VerySans-BoldText.ttf') format('truetype');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Very Sans Headline';
    src: url('/fonts/VerySans-Headline.ttf') format('truetype');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Very Sans Medium', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    line-height: 1.6;
    color: #ffffff;
    background: linear-gradient(135deg, #020611 0%, #050B15 50%, #020611 100%);
    overflow-x: hidden;
  }
`;

const fadeIn = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(30px);
  }
  to { 
    opacity: 1; 
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const shimmer = keyframes`
  0% { 
    background-position: -200% 0; 
  }
  100% { 
    background-position: 200% 0; 
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const pulse = keyframes`
  0%, 100% { 
    opacity: 1; 
    transform: scale(1);
  }
  50% { 
    opacity: 0.8;
    transform: scale(0.95);
  }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #020611 0%, #050B15 50%, #020611 100%);
  position: relative;
  font-family: 'Very Sans Medium', sans-serif;
  overflow: hidden;
`;

const GradientOrb = styled.div`
  position: absolute;
  width: 800px;
  height: 800px;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.3;
  pointer-events: none;
  animation: ${float} 8s ease-in-out infinite;
  
  &.orb-1 {
    top: -20%;
    left: -10%;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.4) 0%, transparent 70%);
    animation-delay: 0s;
  }
  
  &.orb-2 {
    bottom: -20%;
    right: -10%;
    background: radial-gradient(circle, rgba(139, 92, 246, 0.4) 0%, transparent 70%);
    animation-delay: 2s;
  }
  
  @media (max-width: 768px) {
    width: 500px;
    height: 500px;
  }
`;

const Header = styled.header`
  position: relative;
  z-index: 10;
  padding: 2rem 0;
  text-align: center;
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, #3B82F6, transparent);
  }
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const Logo = styled.h1`
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 2.5rem;
  font-weight: 600;
  margin: 0;
  color: #ffffff;
  letter-spacing: -0.02em;
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const LogoIcon = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
  }
`;

const Tagline = styled.p`
  font-family: 'Very Sans Light', sans-serif;
  font-size: 0.9rem;
  color: #94A3B8;
  margin: 0.75rem 0 0 0;
  text-transform: uppercase;
  letter-spacing: 2px;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    letter-spacing: 1.5px;
  }
`;

const MainContent = styled.main`
  position: relative;
  z-index: 2;
  max-width: 1400px;
  margin: 0 auto;
  padding: 3rem 2rem;
  
  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

const WelcomeSection = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  animation: ${fadeIn} 0.6s ease-out;
  
  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const WelcomeTitle = styled.h2`
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 2.5rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 1rem 0;
  
  .highlight {
    background: linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const WelcomeSubtitle = styled.p`
  font-family: 'Very Sans Light', sans-serif;
  font-size: 1.1rem;
  color: #94A3B8;
  max-width: 600px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CardShowcase = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 2rem;
  margin-bottom: 3rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const CardDisplaySection = styled.div`
  animation: ${slideInLeft} 0.8s ease-out;
  animation-delay: 0.2s;
  animation-fill-mode: both;
`;

const CardContainer = styled.div`
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(30px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  padding: 2.5rem;
  position: relative;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #3B82F6, #8B5CF6, #3B82F6);
    background-size: 200% 100%;
    animation: ${shimmer} 3s linear infinite;
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const CardLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  h3 {
    font-family: 'Very Sans Bold', sans-serif;
    font-size: 1.25rem;
    font-weight: 600;
    color: #ffffff;
    margin: 0;
  }
  
  @media (max-width: 768px) {
    h3 {
      font-size: 1.1rem;
    }
  }
`;

const CardBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(59, 130, 246, 0.15);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 20px;
  padding: 0.4rem 0.9rem;
  color: #3B82F6;
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
    padding: 0.35rem 0.75rem;
  }
`;

const VirtualCard = styled.div`
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border-radius: 20px;
  padding: 2rem;
  position: relative;
  overflow: hidden;
  aspect-ratio: 1.6;
  box-shadow: 
    0 20px 60px rgba(0, 0, 0, 0.5),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: linear-gradient(90deg, #3B82F6, #8B5CF6);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 1.5rem;
    right: 1.5rem;
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%);
    border-radius: 10px;
    opacity: 0.9;
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    
    &::after {
      width: 40px;
      height: 40px;
      top: 1rem;
      right: 1rem;
    }
  }
`;

const CardBrand = styled.div`
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  color: #3B82F6;
  margin-bottom: 2rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    margin-bottom: 1.5rem;
  }
`;

const CardNumber = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 1.3rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 2.5rem;
  letter-spacing: 3px;
  line-height: 1.6;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 2rem;
    letter-spacing: 2px;
  }
`;

const CardBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const CardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const CardInfoLabel = styled.div`
  font-family: 'Very Sans Light', sans-serif;
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 1px;
  
  @media (max-width: 768px) {
    font-size: 0.65rem;
  }
`;

const CardInfoValue = styled.div`
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  color: #ffffff;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const QuickActionsSection = styled.div`
  animation: ${slideInRight} 0.8s ease-out;
  animation-delay: 0.3s;
  animation-fill-mode: both;
`;

const QuickActionsCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(30px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  padding: 2rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const QuickActionsTitle = styled.h3`
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 1.25rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const ActionButton = styled.button`
  background: ${props => props.primary 
    ? 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)' 
    : 'rgba(255, 255, 255, 0.05)'};
  color: ${props => props.primary ? 'white' : '#94A3B8'};
  border: ${props => props.primary ? 'none' : '1px solid rgba(255, 255, 255, 0.1)'};
  border-radius: 12px;
  padding: 1rem 1.5rem;
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  width: 100%;
  box-shadow: ${props => props.primary 
    ? '0 10px 30px rgba(59, 130, 246, 0.3)' 
    : 'none'};
  
  &:hover:not(:disabled) {
    background: ${props => props.primary 
      ? 'linear-gradient(135deg, #2563EB 0%, #1d4ed8 100%)' 
      : 'rgba(255, 255, 255, 0.08)'};
    color: ${props => props.primary ? 'white' : '#ffffff'};
    transform: translateY(-2px);
    box-shadow: ${props => props.primary 
      ? '0 15px 40px rgba(59, 130, 246, 0.4)' 
      : '0 5px 20px rgba(0, 0, 0, 0.2)'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.9rem 1.25rem;
  }
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  animation: ${fadeIn} 0.8s ease-out;
  animation-delay: 0.4s;
  animation-fill-mode: both;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const DetailsCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(30px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  padding: 2rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #3B82F6, #8B5CF6, #3B82F6);
    background-size: 200% 100%;
    animation: ${shimmer} 3s linear infinite;
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const DetailsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 2rem;
  
  h3 {
    font-family: 'Very Sans Bold', sans-serif;
    font-size: 1.25rem;
    font-weight: 600;
    color: #ffffff;
    margin: 0;
    
    span {
      color: #3B82F6;
    }
  }
  
  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
    
    h3 {
      font-size: 1.1rem;
    }
  }
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    padding: 0.85rem 0;
  }
`;

const DetailLabel = styled.span`
  font-family: 'Very Sans Light', sans-serif;
  font-size: 0.95rem;
  color: #94A3B8;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const DetailValue = styled.span`
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  color: #ffffff;
  text-align: right;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: 'Very Sans Bold', sans-serif;
  background: ${props => props.active 
    ? 'rgba(16, 185, 129, 0.15)' 
    : 'rgba(239, 68, 68, 0.15)'};
  border: 1px solid ${props => props.active 
    ? 'rgba(16, 185, 129, 0.3)' 
    : 'rgba(239, 68, 68, 0.3)'};
  color: ${props => props.active ? '#10b981' : '#ef4444'};
  
  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
    animation: ${props => props.active ? 'none' : `${pulse} 2s infinite`};
  }
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 0.4rem 0.85rem;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 1.5rem;
  animation: ${fadeIn} 0.6s ease-out;
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid rgba(59, 130, 246, 0.2);
  border-left: 4px solid #3B82F6;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const LoadingText = styled.p`
  font-family: 'Very Sans Medium', sans-serif;
  font-size: 1rem;
  color: #94A3B8;
`;

const ErrorContainer = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 16px;
  padding: 2rem;
  text-align: center;
  animation: ${fadeIn} 0.6s ease-out;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const ErrorMessage = styled.p`
  font-family: 'Very Sans Medium', sans-serif;
  font-size: 1rem;
  color: #ef4444;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const MyCard = () => {
    const [cardDetails, setCardDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activating, setActivating] = useState(false);
    const [cardId, setCardId] = useState(null);
    const { user } = useAuth();

    const activateCard = async () => {
        try {
            setActivating(true);
            setError('');
            
            console.log('Activating card for user:', user?.email);
            
            const response = await axios.post('https://chainfree.info:7001/user/activate-card', {}, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                }
            });
            
            if (response.data.success) {
                console.log('✅ Card activated successfully');
                await fetchUserCardDetails();
                alert('Card activated successfully! Your card is now ready to use.');
            } else {
                setError(response.data.message || 'Failed to activate card');
            }
            
        } catch (error) {
            console.error('❌ Error activating card:', error);
            
            if (error.response?.status === 401) {
                setError('Please log in to activate your card.');
            } else if (error.response?.data?.message) {
                setError(error.response.data.message);
            } else {
                setError('Failed to activate card. Please try again.');
            }
        } finally {
            setActivating(false);
        }
    };

    const fetchUserCardDetails = async () => {
        try {
            setLoading(true);
            setError('');
            
            console.log('Fetching card for user:', user?.email);
            
            const userCardsResponse = await axios.get('https://chainfree.info:7001/user/cards', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                }
            });
            
            if (!userCardsResponse.data.success || userCardsResponse.data.data.length === 0) {
                setError('No virtual card found for your account. Please create a card first.');
                setLoading(false);
                return;
            }
            
            const userCard = userCardsResponse.data.data[0];
            const cardId = userCard.card_id;
            setCardId(userCard.card_id);
            
            console.log('Found user card:', cardId);
            
            const cardDetailsResponse = await axios.post('https://chainfree.info:7000/retrieve-card-details', {
                cardId: cardId,
            });
            
            const data = cardDetailsResponse.data;
            console.log('Card Details from Stripe:', data);
            
            if (data.success === false) {
                setError(data.message || 'Failed to retrieve card details.');
            } else if (data.cardDetails || data.card || data) {
                const cardInfo = data.cardDetails || data.card || data;
                setCardDetails(cardInfo);
                localStorage.setItem('cardId', cardId);
            } else {
                setError('No card details found in response.');
            }
            
        } catch (error) {
            console.error('Error fetching user card details:', error);
            
            if (error.response?.status === 401) {
                setError('Please log in to view your card details.');
            } else if (error.response?.data?.message) {
                setError(error.response.data.message);
            } else {
                setError('Failed to fetch card details. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user) {
            fetchUserCardDetails();
        } else {
            setError('Please log in to view your card details.');
            setLoading(false);
        }
    }, [user]);

    const handleRefresh = () => {
        if (user) {
            fetchUserCardDetails();
        }
    };

    const formatDate = (timestamp) => {
        return new Date(timestamp * 1000).toLocaleDateString();
    };

    if (!user) {
        return (
            <>
                <GlobalStyle />
                <Container>
                    <GradientOrb className="orb-1" />
                    <GradientOrb className="orb-2" />
                    
                    <Header>
                        <HeaderContent>
                            <Logo>
                                <LogoIcon>
                                    <CreditCard size={24} color="#ffffff" />
                                </LogoIcon>
                                HydrixPay
                            </Logo>
                            <Tagline>Virtual Card Management</Tagline>
                        </HeaderContent>
                    </Header>

                    <MainContent>
                        <ErrorContainer>
                            <ErrorMessage>
                                Please log in to view your virtual card details.
                            </ErrorMessage>
                        </ErrorContainer>
                    </MainContent>
                </Container>
            </>
        );
    }

    return (
        <>
            <GlobalStyle />
            <Container>
                <GradientOrb className="orb-1" />
                <GradientOrb className="orb-2" />
                
                <Header>
                    <HeaderContent>
                        <Logo>
                            <LogoIcon>
                                <CreditCard size={24} color="#ffffff" />
                            </LogoIcon>
                            HydrixPay
                        </Logo>
                        <Tagline>Virtual Card Management</Tagline>
                    </HeaderContent>
                </Header>

                <MainContent>
                    {loading && (
                        <LoadingContainer>
                            <LoadingSpinner />
                            <LoadingText>Loading your card details...</LoadingText>
                        </LoadingContainer>
                    )}
                    
                    {error && !loading && (
                        <ErrorContainer>
                            <ErrorMessage>{error}</ErrorMessage>
                            <ActionButton primary onClick={handleRefresh}>
                                <RefreshCw size={18} />
                                Try Again
                            </ActionButton>
                        </ErrorContainer>
                    )}
                    
                    {cardDetails && !loading && (
                        <>
                            <WelcomeSection>
                                <WelcomeTitle>
                                    Welcome back, <span className="highlight">{user.full_name?.split(' ')[0] || 'User'}</span>
                                </WelcomeTitle>
                                <WelcomeSubtitle>
                                    Manage your virtual card and track your spending all in one place
                                </WelcomeSubtitle>
                            </WelcomeSection>

                            <CardShowcase>
                                <CardDisplaySection>
                                    <CardContainer>
                                        <CardHeader>
                                            <CardLabel>
                                                <Sparkles size={20} color="#3B82F6" />
                                                <h3>Your Virtual Card</h3>
                                            </CardLabel>
                                            <CardBadge>
                                                <StatusBadge active={cardDetails.status === 'active'}>
                                                    {cardDetails.status}
                                                </StatusBadge>
                                            </CardBadge>
                                        </CardHeader>
                                        
                                        <VirtualCard>
                                            <CardBrand>HydrixPay</CardBrand>
                                            <CardNumber>
                                                {cardDetails.card_number}
                                            </CardNumber>
                                            <CardBottom>
                                                <CardInfo>
                                                    <CardInfoLabel>Cardholder</CardInfoLabel>
                                                    <CardInfoValue>
                                                        {cardDetails.cardholder_name || user.full_name || 'Card Holder'}
                                                    </CardInfoValue>
                                                </CardInfo>
                                                <CardInfo>
                                                    <CardInfoLabel>Expires</CardInfoLabel>
                                                    <CardInfoValue>
                                                        {String(cardDetails.exp_month).padStart(2, '0')}/{String(cardDetails.exp_year).slice(-2)}
                                                    </CardInfoValue>
                                                </CardInfo>
                                            </CardBottom>
                                        </VirtualCard>
                                    </CardContainer>
                                    
                                    <div style={{ marginTop: '2rem' }}>
                                        <CardBalance cardId={cardId} />
                                    </div>
                                </CardDisplaySection>
                                
                                <QuickActionsSection>
                                    <QuickActionsCard>
                                        <QuickActionsTitle>
                                            <Clock size={20} color="#3B82F6" />
                                            Quick Actions
                                        </QuickActionsTitle>
                                        
                                        {cardDetails.status === 'inactive' && (
                                            <ActionButton 
                                                primary
                                                onClick={activateCard} 
                                                disabled={activating}
                                            >
                                                <CheckCircle size={18} />
                                                {activating ? 'Activating...' : 'Activate Card'}
                                            </ActionButton>
                                        )}
                                        
                                        <ActionButton onClick={handleRefresh}>
                                            <RefreshCw size={18} />
                                            Refresh Details
                                        </ActionButton>
                                    </QuickActionsCard>
                                </QuickActionsSection>
                            </CardShowcase>

                            <DetailsGrid>
                                <DetailsCard>
                                    <DetailsHeader>
                                        <CreditCard size={20} color="#3B82F6" />
                                        <h3>Card <span>Information</span></h3>
                                    </DetailsHeader>
                                    
                                    <DetailRow>
                                        <DetailLabel>Account Holder</DetailLabel>
                                        <DetailValue>{user.full_name}</DetailValue>
                                    </DetailRow>
                                    
                                    <DetailRow>
                                        <DetailLabel>Card ID</DetailLabel>
                                        <DetailValue>••••••••</DetailValue>
                                    </DetailRow>
                                    
                                    <DetailRow>
                                        <DetailLabel>Brand</DetailLabel>
                                        <DetailValue>{cardDetails.brand}</DetailValue>
                                    </DetailRow>
                                    
                                    <DetailRow>
                                        <DetailLabel>Type</DetailLabel>
                                        <DetailValue>Virtual</DetailValue>
                                    </DetailRow>
                                </DetailsCard>

                                <DetailsCard>
                                    <DetailsHeader>
                                        <CheckCircle size={20} color="#3B82F6" />
                                        <h3>Status & <span>Activity</span></h3>
                                    </DetailsHeader>
                                    
                                    <DetailRow>
                                        <DetailLabel>Status</DetailLabel>
                                        <DetailValue>
                                            <StatusBadge active={cardDetails.status === 'active'}>
                                                {cardDetails.status}
                                            </StatusBadge>
                                        </DetailValue>
                                    </DetailRow>
                                    
                                    <DetailRow>
                                        <DetailLabel>Created</DetailLabel>
                                        <DetailValue>{formatDate(cardDetails.created)}</DetailValue>
                                    </DetailRow>
                                    
                                    <DetailRow>
                                        <DetailLabel>Last Updated</DetailLabel>
                                        <DetailValue>Just now</DetailValue>
                                    </DetailRow>
                                </DetailsCard>
                            </DetailsGrid>
                        </>
                    )}
                </MainContent>
            </Container>
        </>
    );
};

export default MyCard;